import { PetAvatar } from 'components/Pet/Avatar/PetAvatar';
import { useOnlineBookingState } from 'hooks/useOnlineBookingState';
import React from 'react';
import { useSelector } from 'react-redux';
import { getPurchaseCart } from 'reducers/purchases/PurchasesSelectors';
import {
    formatPurchaseAmount,
    getPurchaseOffer,
    PurchaseOffer,
    PurchaseOfferKinds
} from 'types/Purchase';
import classes from './PurchaseCart.module.scss';

interface PurchaseCartProps {
    // The pet being shopped for shows up as soon as the cart has something, like the design
    activePetUuid?: string;
    // Checkout lists every item under Packages and Memberships, without the pets
    flat?: boolean;
}

const formatCartLinePrice = (offer: PurchaseOffer) =>
    offer.kind === PurchaseOfferKinds.Membership
        ? `${formatPurchaseAmount(offer.price)}/mo`
        : formatPurchaseAmount(offer.price);

const CartSections: React.FC<{ offers: Array<PurchaseOffer> }> = ({ offers }) => {
    const sections = [
        {
            title: 'Packages',
            offers: offers.filter(offer => offer.kind === PurchaseOfferKinds.Package)
        },
        {
            title: 'Memberships',
            offers: offers.filter(offer => offer.kind === PurchaseOfferKinds.Membership)
        }
    ].filter(section => section.offers.length > 0);

    return (
        <>
            {sections.map(section => (
                <div key={section.title} className={classes.section}>
                    <p className={classes.subtitle}>{section.title}</p>

                    <div className="flex flex-col gap-2">
                        {section.offers.map((offer, index) => (
                            <div key={`${offer.id}-${index}`} className={classes.itemLine}>
                                <span>{offer.name}</span>
                                <span>{formatCartLinePrice(offer)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </>
    );
};

export const PurchaseCart: React.FC<PurchaseCartProps> = ({ activePetUuid, flat = false }) => {
    const { selectedPets } = useOnlineBookingState();
    const cart = useSelector(getPurchaseCart);

    const petsWithOffers = selectedPets.map(pet => ({
        pet,
        offers: (cart[pet.uuid] ?? []).flatMap(offerId => {
            const offer = getPurchaseOffer(offerId);

            return offer ? [offer] : [];
        })
    }));

    const cartHasItems = petsWithOffers.some(({ offers }) => offers.length > 0);

    const visiblePets = petsWithOffers.filter(
        ({ pet, offers }) => offers.length > 0 || (cartHasItems && pet.uuid === activePetUuid)
    );

    return (
        <div className={classes.root}>
            <h1 className={classes.title}>Your Cart</h1>

            {flat && cartHasItems && (
                <div className={classes.flatItems}>
                    <CartSections offers={petsWithOffers.flatMap(({ offers }) => offers)} />
                </div>
            )}

            {!flat &&
                visiblePets.map(({ pet, offers }) => (
                    <div key={pet.uuid} className={classes.petItem}>
                        <div className={classes.pet}>
                            <PetAvatar size={56} image={pet.image?.src} petType={pet.type} />

                            <div className={classes.petInfo}>
                                <p className={classes.petName}>{pet.name}</p>
                                <p className={classes.petBreed}>
                                    {pet.breed?.name ?? 'Not Defined'}
                                </p>
                            </div>
                        </div>

                        <CartSections offers={offers} />
                    </div>
                ))}
        </div>
    );
};
